import { onBeforeUnmount, watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'

interface DismissableOptions {
    isOpen: Ref<boolean>
    onDismiss: () => void
    // The elements a pointerdown may land on WITHOUT dismissing (the panel, its trigger).
    inside: () => Array<HTMLElement | null | undefined>
}

const hasDocument = () => typeof document !== 'undefined'

// Shared close rules for every floating surface (menus, sheets, the typeahead,
// the station menu): Escape, pointerdown outside, route change. Listeners live
// only while the surface is open, and only in a browser.
export function useDismissable({ isOpen, onDismiss, inside }: DismissableOptions): void {
    const route = useRoute()

    function onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') onDismiss()
    }

    function onPointerdown(event: Event) {
        const target = event.target as Node | null
        if (!target) return
        const isInside = inside().some(el => el && el.contains(target))
        if (!isInside) onDismiss()
    }

    function attach() {
        if (!hasDocument()) return
        document.addEventListener('keydown', onKeydown)
        document.addEventListener('pointerdown', onPointerdown, true)
    }

    function detach() {
        if (!hasDocument()) return
        document.removeEventListener('keydown', onKeydown)
        document.removeEventListener('pointerdown', onPointerdown, true)
    }

    watch(isOpen, open => (open ? attach() : detach()), { immediate: true })

    if (route) {
        watch(
            () => route.fullPath,
            () => {
                if (isOpen.value) onDismiss()
            }
        )
    }

    onBeforeUnmount(detach)
}
